// Traffic Signs imagery panels.
//
// Two flat tenant-bucket prefixes beside source/: images/ (camera frames) and
// camera-data/ (registration CSVs + .cam/.cal/.4x4/.eif). Counts only — a road
// can carry thousands of frames, so there is no per-file table. Rendered only
// for S3-source projects; Drive projects never see it.
//
// PRESENTATIONAL (2026-09-09). Summary fetching moved to useImagerySummaries so
// the same counts drive both this panel and the readiness chip on the TSA task
// card; two fetchers would drift apart after an upload. This component owns only
// its own upload/clear interactions and calls `refresh` when it changes S3.
//
// These panels used to live permanently in the wizard's Upload step. Most users
// never touch Traffic Signs, so they now render inside ImageryUploadModal, which
// opens from the TSA task card.
import React, { useCallback, useState } from 'react';
import UploadModal from '../../Projects/ProjectView/FileExplorer/UploadModal';
import fileService from '../../../services/fileService';
import { IMAGERY_TARGETS } from './useImagerySummaries';

export { IMAGERY_TARGETS };

const formatBytes = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
};

export default function ImageryUploadPanels({
  projectId,
  sourceStorage,
  onNotify,
  summaries = {},
  loading = {},
  fetchErrors = {},
  refresh,
}) {
  const [openModal, setOpenModal] = useState(null);  // prefix | null
  const [clearing, setClearing] = useState(null);    // prefix | null

  const handleClear = useCallback(async (target) => {
    const current = summaries[target.prefix] || { count: 0 };
    if (current.count === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete all ${current.count} file(s) under ${target.prefix}? This cannot be undone.`)) return;
    setClearing(target.prefix);
    try {
      const res = await fileService.deleteS3Prefix(projectId, target.prefix);
      const errors = Array.isArray(res?.errors) ? res.errors : [];
      if (errors.length > 0 && typeof onNotify === 'function') {
        onNotify(`Cleared ${target.prefix} with ${errors.length} error(s).`);
      }
    } catch (err) {
      if (typeof onNotify === 'function') onNotify(err?.message || `Failed to clear ${target.prefix}`);
    } finally {
      setClearing(null);
      if (typeof refresh === 'function') refresh(target.prefix);
    }
  }, [projectId, summaries, onNotify, refresh]);

  if (sourceStorage !== 's3' || !projectId) return null;

  return (
    <div className="rsa-upload-step-section rsa-imagery-panels">
      {IMAGERY_TARGETS.map((target) => {
        const s = summaries[target.prefix] || { count: 0, bytes: 0 };
        const busy = !!loading[target.prefix];
        const fetchError = fetchErrors[target.prefix] || null;
        // A failed check means the displayed count can't be trusted, so the
        // Clear action (which deletes by prefix, not by the stale count) stays
        // hidden until a fetch succeeds again.
        const canClear = !fetchError && s.count > 0;
        return (
          <div key={target.key} className="rsa-upload-step-status rsa-upload-step-status-toggle">
            <div className="rsa-upload-step-status-copy">
              <div className="rsa-upload-step-status-copy-header">
                <p className="rsa-upload-step-heading">{target.title}</p>
              </div>
              <p className="rsa-upload-step-hint">{target.hint}</p>
            </div>
            <span className="rsa-upload-step-status-row">
              <span className="rsa-upload-step-status-summary">
                <i
                  className={`fas ${
                    busy
                      ? 'fa-spinner fa-spin'
                      : fetchError
                        ? 'fa-circle-exclamation error'
                        : s.count > 0
                          ? 'fa-circle-check ready'
                          : 'fa-triangle-exclamation empty'
                  } rsa-upload-step-status-icon`}
                  aria-hidden
                />
                <span className="rsa-upload-step-status-text">
                  {busy
                    ? 'Checking…'
                    : fetchError
                      ? `Couldn't check ${target.prefix} — ${fetchError}`
                      : s.count > 0
                        ? `${s.count} file${s.count === 1 ? '' : 's'} · ${formatBytes(s.bytes)}`
                        : `No files under ${target.prefix} yet`}
                </span>
              </span>
              <span className="rsa-upload-step-status-actions">
                <span
                  className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary rsa-upload-step-status-action"
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => { e.stopPropagation(); setOpenModal(target.prefix); }}
                  title={`${target.button} into ${target.prefix}`}
                >
                  <i className="fas fa-upload" aria-hidden /> {target.button}
                </span>
                {canClear && (
                  <button
                    type="button"
                    className="rsa-upload-step-files-toolbar-btn rsa-upload-step-files-toolbar-btn-danger"
                    onClick={(e) => { e.stopPropagation(); handleClear(target); }}
                    disabled={clearing === target.prefix}
                    title={`Delete everything under ${target.prefix}`}
                  >
                    <i className={`fas ${clearing === target.prefix ? 'fa-spinner fa-spin' : 'fa-trash'}`} aria-hidden /> Clear
                  </button>
                )}
              </span>
            </span>
            <UploadModal
              isOpen={openModal === target.prefix}
              onClose={() => setOpenModal(null)}
              onUpload={async () => {
                setOpenModal(null);
                if (typeof refresh === 'function') await refresh(target.prefix);
              }}
              projectId={projectId}
              currentFolderId={projectId}
              currentFolderGoogleDriveId={null}
              projectName={null}
              currentFolderName={`Source / ${target.title}`}
              sourceStorage={sourceStorage}
              s3TargetPrefix={target.prefix}
              accept={target.accept}
            />
          </div>
        );
      })}
    </div>
  );
}
